import { useEffect, useRef, useState } from "react";
import { NavLink, useNavigate, useParams } from "react-router-dom";
import { useAuth } from "../lib/auth.tsx";
import { useTheme } from "../lib/theme.tsx";
import { authFetch } from "../lib/api.ts";
import { BellIcon, ChevronRightIcon, PlusIcon } from "./Icon.tsx";

interface TopBarProps {
  onNewAppointment?: () => void;
}

interface BusinessLite {
  id: string;
  slug: string;
  name: string;
  role: string;
}

const TABS = [
  { label: "Today", path: "" },
  { label: "Calendar", path: "calendar" },
  { label: "Services", path: "services" },
  { label: "Staff", path: "staff" },
  { label: "Customers", path: "customers" },
  { label: "Products", path: "products" },
  { label: "Payments", path: "payments" },
  { label: "Team", path: "team" },
  { label: "Settings", path: "settings" },
];

export function TopBar({ onNewAppointment }: TopBarProps = {}) {
  const { slug } = useParams<{ slug: string }>();
  const { session, signOut } = useAuth();
  const { theme, toggle } = useTheme();
  const navigate = useNavigate();
  const [profileOpen, setProfileOpen] = useState(false);
  const [bizOpen, setBizOpen] = useState(false);
  const [businesses, setBusinesses] = useState<BusinessLite[] | null>(null);
  const [loadErr, setLoadErr] = useState<string | null>(null);
  const profileRef = useRef<HTMLDivElement>(null);
  const bizRef = useRef<HTMLDivElement>(null);

  // Close on outside-click / Esc.
  useEffect(() => {
    if (!profileOpen && !bizOpen) return;
    const onClick = (e: MouseEvent) => {
      const t = e.target as Node;
      if (!profileRef.current?.contains(t)) setProfileOpen(false);
      if (!bizRef.current?.contains(t)) setBizOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setProfileOpen(false);
        setBizOpen(false);
      }
    };
    document.addEventListener("mousedown", onClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [profileOpen, bizOpen]);

  // Load the businesses list eagerly when a tenant is active so we can show
  // its name in the header (not the slug).
  useEffect(() => {
    if (!slug || businesses !== null) return;
    authFetch("/businesses")
      .then((r) => r.json())
      .then((rows: BusinessLite[]) => setBusinesses(rows))
      .catch((err: Error) => setLoadErr(err.message));
  }, [slug, businesses]);

  const handleSignOut = async () => {
    setProfileOpen(false);
    await signOut();
    navigate("/login", { replace: true });
  };

  const email = session?.user?.email ?? "";
  const fullName = (session?.user?.user_metadata?.full_name as string | undefined) ?? "";
  const initials = email ? email.slice(0, 2).toUpperCase() : "?";
  const currentBiz = businesses?.find((b) => b.slug === slug) ?? null;
  const currentBizName = currentBiz?.name ?? slug ?? "";

  return (
    <header className="zd-mast sticky top-0 z-30 shrink-0">
      <div className="zd-mast-inner">
        <span className="zd-brand" onClick={() => navigate("/")} style={{ cursor: "pointer" }}>
          Zedule<span className="dot">.</span>
        </span>

        {/* Inline section tabs (replaces the separate TabNav row) */}
        {slug && (
          <nav className="zd-tabs-inline hidden lg:flex" aria-label="Sections">
            {TABS.map((tab) => {
              const to = `/${slug}${tab.path ? `/${tab.path}` : ""}`;
              return (
                <NavLink
                  key={tab.path}
                  to={to}
                  end={tab.path === ""}
                  className={({ isActive }) => (isActive ? "zd-tab active" : "zd-tab")}
                >
                  {tab.label}
                </NavLink>
              );
            })}
          </nav>
        )}

        <span className="flex-1" />

        {/* Business dropdown — shows current biz NAME, opens list of all */}
        {slug && (
          <div ref={bizRef} style={{ position: "relative" }}>
            <button
              type="button"
              onClick={() => setBizOpen((v) => !v)}
              aria-haspopup="menu"
              aria-expanded={bizOpen}
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 8,
                background: "transparent",
                border: "1px solid var(--rule)",
                borderRadius: 999,
                padding: "5px 10px 5px 14px",
                fontSize: 13.5,
                fontWeight: 500,
                color: "var(--ink)",
                cursor: "pointer",
                maxWidth: 240,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
              title={currentBizName}
            >
              <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {currentBizName}
              </span>
              <span
                aria-hidden
                style={{
                  display: "inline-block",
                  transform: "rotate(90deg)",
                  color: "var(--ink-soft)",
                  fontSize: 11,
                }}
              >
                <ChevronRightIcon size={12} />
              </span>
            </button>
            {bizOpen && (
              <div
                role="menu"
                style={{
                  position: "absolute",
                  top: "calc(100% + 8px)",
                  right: 0,
                  width: 280,
                  background: "var(--paper-raised)",
                  borderRadius: 12,
                  boxShadow: "var(--shadow-md)",
                  padding: 6,
                  zIndex: 40,
                }}
              >
                <div style={menuLabelStyle}>Your businesses</div>
                {businesses === null && !loadErr && (
                  <div style={{ padding: "8px 12px", fontSize: 12.5, color: "var(--ink-faint)" }}>Loading…</div>
                )}
                {loadErr && (
                  <div style={{ padding: "8px 12px", fontSize: 12.5, color: "var(--status-cancelled)" }}>{loadErr}</div>
                )}
                {businesses && businesses.length === 0 && (
                  <div style={{ padding: "6px 12px 8px", fontSize: 12.5, color: "var(--ink-faint)" }}>
                    No businesses yet.
                  </div>
                )}
                {businesses?.map((biz) => {
                  const isCurrent = biz.slug === slug;
                  return (
                    <button
                      key={biz.id}
                      role="menuitem"
                      onClick={() => {
                        setBizOpen(false);
                        navigate(`/${biz.slug}`);
                      }}
                      style={{
                        ...menuItemStyle,
                        display: "flex",
                        alignItems: "center",
                        gap: 10,
                        background: isCurrent ? "var(--paper-well)" : "transparent",
                      }}
                    >
                      <span
                        style={{
                          width: 26,
                          height: 26,
                          borderRadius: "50%",
                          background: "var(--accent)",
                          color: "white",
                          display: "inline-flex",
                          alignItems: "center",
                          justifyContent: "center",
                          fontSize: 11,
                          fontWeight: 600,
                          flexShrink: 0,
                        }}
                      >
                        {biz.name.trim()[0]?.toUpperCase() ?? "?"}
                      </span>
                      <span style={{ flex: 1, minWidth: 0, overflow: "hidden" }}>
                        <span
                          style={{
                            display: "block",
                            fontSize: 13,
                            color: "var(--ink)",
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                          }}
                        >
                          {biz.name}
                        </span>
                        <span style={{ display: "block", fontSize: 11, color: "var(--ink-faint)", textTransform: "capitalize" }}>
                          {biz.role}
                        </span>
                      </span>
                      {isCurrent && (
                        <span
                          style={{
                            fontFamily: "'JetBrains Mono', ui-monospace, monospace",
                            fontSize: 9,
                            color: "var(--ink-faint)",
                            letterSpacing: "0.1em",
                            textTransform: "uppercase",
                          }}
                        >
                          current
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        )}

        <button className="zd-icon-btn" title="Notifications" aria-label="Notifications">
          <BellIcon />
        </button>

        {slug && onNewAppointment && (
          <button onClick={onNewAppointment} className="zd-cta hidden md:inline-flex">
            <PlusIcon /> New
          </button>
        )}

        {/* Profile menu — theme toggle lives inside */}
        <div ref={profileRef} style={{ position: "relative" }}>
          <button
            type="button"
            className="zd-avatar"
            aria-label="Profile menu"
            aria-haspopup="menu"
            aria-expanded={profileOpen}
            onClick={() => setProfileOpen((v) => !v)}
            style={{ cursor: "pointer", border: "none", padding: 0 }}
          >
            {initials}
          </button>
          {profileOpen && (
            <div
              role="menu"
              style={{
                position: "absolute",
                top: "calc(100% + 8px)",
                right: 0,
                width: 280,
                background: "var(--paper-raised)",
                borderRadius: 12,
                boxShadow: "var(--shadow-md)",
                padding: 6,
                zIndex: 40,
              }}
            >
              <div style={{ padding: "10px 12px 12px", borderBottom: "1px solid var(--rule)", marginBottom: 6 }}>
                {fullName && <div style={{ fontSize: 13.5, fontWeight: 500, color: "var(--ink)" }}>{fullName}</div>}
                {email && (
                  <div style={{ fontSize: 12, color: "var(--ink-faint)", marginTop: 2, wordBreak: "break-all" }}>
                    {email}
                  </div>
                )}
              </div>

              <button
                role="menuitem"
                onClick={() => {
                  toggle();
                }}
                style={menuItemStyle}
              >
                {theme === "dark" ? "Switch to light mode" : "Switch to dark mode"}
              </button>

              <button
                role="menuitem"
                onClick={() => {
                  setProfileOpen(false);
                  navigate("/create-business");
                }}
                style={menuItemStyle}
              >
                + Add a new business
              </button>

              <hr style={{ border: "none", borderTop: "1px solid var(--rule)", margin: "4px 0" }} />
              <button role="menuitem" onClick={handleSignOut} style={menuItemStyle}>
                Sign out
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}

const menuItemStyle: React.CSSProperties = {
  display: "block",
  width: "100%",
  textAlign: "left",
  padding: "8px 12px",
  background: "transparent",
  border: "none",
  borderRadius: 8,
  fontSize: 13.5,
  color: "var(--ink)",
  cursor: "pointer",
};

const menuLabelStyle: React.CSSProperties = {
  fontFamily: "'JetBrains Mono', ui-monospace, monospace",
  fontSize: 10,
  fontWeight: 500,
  letterSpacing: "0.16em",
  textTransform: "uppercase",
  color: "var(--ink-faint)",
  margin: "4px 8px 6px",
};
